import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Printer } from "lucide-react";
import { TrainingTour } from "../tour/TrainingTour";
import { useTrainingTour } from "../tour/useTrainingTour";
import { serviceStandardPdfPreviewTourSteps } from "../tour/trainingTourSteps";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useStandardDetailQuery } from "../api/queries";
import { trainingRoutes } from "../trainingRoutes";
import { TrainingServiceStandardPdfPreviewContent } from "../ui/TrainingServiceStandardPdfPreviewContent";
import { mapApiToServiceStandardDetail } from "../lib/mapServiceStandardDetail";

export function ServiceStandardPdfPreviewPage() {
  const tour = useTrainingTour("training-standard-pdf-preview-tour-completed");
  const { id } = useParams({ from: "/app/training/standards/$id/pdf-preview" });
  const navigate = useNavigate({ from: "/app/training/standards/$id/pdf-preview" });

  const { data, isLoading, error } = useStandardDetailQuery(id);

  const handleBack = () => {
    navigate({
      to: trainingRoutes.standardDetail,
      params: { id },
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <>
        <Header className="print:hidden" />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Header className="print:hidden" />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບມາດຕະຖານບໍລິການ
              </h2>
              <p className="mb-4 text-muted-foreground">
                ບໍ່ພົບຂໍ້ມູນ ຫຼື ທ່ານບໍ່ມີສິດເຂົ້າເຖິງ
              </p>
              <Button type="button" onClick={handleBack} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                ກັບໄປທີ່ລາຍລະອຽດ
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  const standard = mapApiToServiceStandardDetail(data as Record<string, unknown>);

  return (
    <>
      <Header className="print:hidden" />
      <Main>
        <div
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden"
          data-tourid="training-standard-pdf-toolbar"
        >
          <Button
            type="button"
            variant="ghost"
            onClick={handleBack}
            className="w-full gap-2 sm:w-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            ກັບໄປທີ່ມາດຕະຖານ
          </Button>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={handlePrint} className="gap-2">
              <Printer className="h-4 w-4" />
              ພິມ
            </Button>
          </div>
        </div>

        <div
          className="mb-4 flex items-center gap-2 print:hidden"
          data-tourid="training-standard-pdf-page-title"
        >
          <h1 className="font-bold text-2xl tracking-tight">{standard.name}</h1>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={tour.startTour}
            aria-label="ເລີ່ມທົດສອບການນຳທາງ"
            title="ເລີ່ມທົດສອບການນຳທາງ"
          >
            <HelpCircle className="size-4" />
          </Button>
        </div>

        <TrainingServiceStandardPdfPreviewContent standard={standard} />

        <TrainingTour
          steps={serviceStandardPdfPreviewTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
